import { useLocaleState, useTranslate } from "ra-core";
import { useCallback, useEffect, useRef, useState } from "react";

import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { restoreFocus } from "./focus";
import { sceneModel } from "./onboardingLogic";
import { useOnboarding, useOnboardingStory } from "./OnboardingProvider";
import { SCENES } from "./scenes";
import type { SceneModel } from "./types";
import "./onboarding.css";

const SCENE_MS = 10_000;

/** Un champ de saisie garde Échap et les flèches pour lui. */
export const isFieldTarget = (target: EventTarget | null) =>
  target instanceof HTMLElement &&
  (["SELECT", "INPUT", "TEXTAREA"].includes(target.tagName) || target.isContentEditable);

/** Un bouton ou un lien garde Espace pour lui (il s'active). */
const isControlTarget = (target: EventTarget | null) =>
  target instanceof HTMLElement && !!target.closest("button, a, select, label, [role='option'], [role='listbox']");

const usePrefersReducedMotion = () => {
  const [reduced, setReduced] = useState(
    () => window.matchMedia("(prefers-reduced-motion: reduce)").matches,
  );
  useEffect(() => {
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduced(query.matches);
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);
  return reduced;
};

/** La séquence d'accueil, montée une fois dans chaque mise en page ; s'ouvre seule à la première connexion. */
export const OnboardingStory = () => {
  const { introOpen, closeIntro, introOrigin } = useOnboarding();
  const isMobile = useIsMobile();
  const translate = useTranslate();
  const [locale] = useLocaleState();
  // Seul le premier chargement est attendu : une relecture en arrière-plan ne démonte pas la séquence.
  const { data: story, isPending } = useOnboardingStory(introOpen);
  if (!introOpen || isPending) return null;
  return (
    <StoryShell
      model={sceneModel(story ?? null)}
      isMobile={isMobile}
      onClose={closeIntro}
      fallbackFocus={introOrigin}
      t={(key, options) => translate(`resources.onboarding.${key}`, options)}
      tr={(key, options) => translate(key, options)}
      locale={locale}
    />
  );
};

type ShellProps = {
  model: SceneModel;
  isMobile: boolean;
  onClose: () => void;
  fallbackFocus?: () => HTMLElement | null;
  t: (key: string, options?: object) => string;
  tr?: (fullKey: string, options?: object) => string;
  locale?: string;
};

export const StoryShell = ({
  model,
  isMobile,
  onClose,
  fallbackFocus,
  t,
  tr = (key) => key,
  locale = "fr",
}: ShellProps) => {
  const [index, setIndex] = useState(0);
  // Pause (WCAG 2.2.2) : appui long sur téléphone ; sur ordinateur, bouton « Pause » ou Espace, et tant que le focus
  // est dans la scène (le focus initial est sur la fenêtre, pas dans la scène : l'avance part sans rien toucher).
  const [held, setHeld] = useState(false);
  const [toggled, setToggled] = useState(false);
  const [focusWithin, setFocusWithin] = useState(false);
  const paused = held || toggled || (!isMobile && focusWithin);
  const reducedMotion = usePrefersReducedMotion();
  const dialog = useRef<HTMLDivElement>(null);
  const last = SCENES.length - 1;

  const next = useCallback(() => setIndex((i) => Math.min(i + 1, last)), [last]);
  const previous = useCallback(() => setIndex((i) => Math.max(i - 1, 0)), []);

  // Focus gardé dans la séquence, rendu à l'élément d'origine à la fermeture ; défilement de la page figé.
  const fallbackRef = useRef(fallbackFocus);
  fallbackRef.current = fallbackFocus;
  useEffect(() => {
    const before = document.activeElement as HTMLElement | null;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    dialog.current?.focus();
    // Le menu du compte (Radix) rend le focus à son bouton après le montage : on le ramène dans la fenêtre.
    const onFocusIn = (event: FocusEvent) => {
      const root = dialog.current;
      if (root && event.target instanceof Node && !root.contains(event.target)) root.focus();
    };
    document.addEventListener("focusin", onFocusIn);
    return () => {
      document.removeEventListener("focusin", onFocusIn);
      document.body.style.overflow = overflow;
      restoreFocus(before, fallbackRef.current?.() ?? null);
    };
  }, []);

  // Une scène qui change, un bouton désactivé ou retiré : si le focus est tombé hors de la fenêtre, on le ramène.
  // Un élément démonté n'émet pas de blur : la pause par le focus est recalculée ici.
  useEffect(() => {
    if (!dialog.current?.contains(document.activeElement)) dialog.current?.focus();
    setFocusWithin(!!document.activeElement?.closest(".onb-stage"));
  }, [index]);

  // Le clavier est écouté sur le document : il reste actif même si le focus a quitté la fenêtre.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Tab") {
        const root = dialog.current;
        if (!root) return;
        const focusable = root.querySelectorAll<HTMLElement>(
          "button:not([disabled]), a, select, [tabindex='0']",
        );
        if (!focusable.length) return;
        const first = focusable[0];
        const end = focusable[focusable.length - 1];
        const active = document.activeElement;
        const inside = root.contains(active) && active !== root;
        if (!inside) {
          (event.shiftKey ? end : first).focus();
          event.preventDefault();
        } else if (event.shiftKey && active === first) {
          end.focus();
          event.preventDefault();
        } else if (!event.shiftKey && active === end) {
          first.focus();
          event.preventDefault();
        }
        return;
      }
      if (isFieldTarget(event.target)) return;
      if (event.key === " ") {
        if (isControlTarget(event.target)) return;
        event.preventDefault();
        setToggled((value) => !value);
      } else if (event.key === "Escape") onCloseRef.current();
      else if (event.key === "ArrowRight") next();
      else if (event.key === "ArrowLeft") previous();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [next, previous]);

  // Téléphone : toucher à gauche revient, à droite avance, appui long met en pause.
  const pressTimer = useRef<number | null>(null);
  const longPress = useRef(false);
  const onPointerDown = () => {
    longPress.current = false;
    pressTimer.current = window.setTimeout(() => {
      longPress.current = true;
      setHeld(true);
    }, 350);
  };
  const onPointerAbort = () => {
    if (pressTimer.current) window.clearTimeout(pressTimer.current);
    if (longPress.current) setHeld(false);
    longPress.current = false;
  };
  const onPointerUp = (event: React.PointerEvent) => {
    if (pressTimer.current) window.clearTimeout(pressTimer.current);
    if (longPress.current) {
      setHeld(false);
      return;
    }
    if ((event.target as HTMLElement).closest("button, a, select, label")) return;
    const { left, width } = event.currentTarget.getBoundingClientRect();
    if (event.clientX - left < width / 3) previous();
    else next();
  };

  const Scene = SCENES[index];
  return (
    <div
      ref={dialog}
      role="dialog"
      aria-modal="true"
      aria-label={t("label")}
      tabIndex={-1}
      className={cn("onb-root", isMobile && "onb-mobile", reducedMotion && "onb-still")}
    >
      <div className="onb-bars" aria-hidden>
        {SCENES.map((_, i) => (
          <i
            key={i}
            className={cn(i < index && "onb-done", i === index && !reducedMotion && "onb-run", paused && "onb-paused")}
            style={i === index ? { animationDuration: `${SCENE_MS}ms` } : undefined}
            // La barre mène le temps : en pause, elle et l'avance s'arrêtent ensemble.
            onAnimationEnd={i === index && i < last ? next : undefined}
          />
        ))}
      </div>
      <div className="onb-top">
        <span className="onb-step">
          <span aria-live="polite">{t(`scene.${index + 1}.of`)}</span>
          <span aria-live="polite" className="onb-paused-label">
            {paused ? t("paused") : ""}
          </span>
        </span>
        <span className="onb-top-actions">
          {!isMobile && (
            <button
              type="button"
              className="onb-skip"
              aria-pressed={toggled}
              onClick={() => setToggled((value) => !value)}
            >
              {t(toggled ? "resume" : "pause")}
            </button>
          )}
          <button type="button" className="onb-skip" onClick={onClose}>
            {t("skip")}
          </button>
        </span>
      </div>
      <div
        className="onb-stage"
        onPointerDown={isMobile ? onPointerDown : undefined}
        onPointerUp={isMobile ? onPointerUp : undefined}
        onPointerCancel={isMobile ? onPointerAbort : undefined}
        onPointerLeave={isMobile ? onPointerAbort : undefined}
        onFocus={() => setFocusWithin(true)}
        onBlur={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setFocusWithin(false);
        }}
      >
        <Scene
          key={index}
          model={model}
          active
          reducedMotion={reducedMotion}
          onClose={onClose}
          t={t}
          tr={tr}
          locale={locale}
        />
      </div>
      {!isMobile && (
        <div className="onb-nav">
          <button type="button" onClick={previous} disabled={index === 0}>
            {t("previous")}
          </button>
          {index < last && (
            <button type="button" className="onb-primary" onClick={next}>
              {t("next")}
            </button>
          )}
        </div>
      )}
    </div>
  );
};
